import { useTranslation } from "react-i18next"
import { Outlet, useLocation, useNavigate, useParams } from "react-router"
import { useCallback, useMemo } from "react"
import { useGetDeatiledSingleFinancialModelQuery } from "@/feature/financial-model/api"
import {
  ModuleNavigation,
  TabValue,
} from "@/components/shared/ModuleNavigation"
import { useGetCogsSuspenseSettings } from "@/feature/financial-model/ecommerce/cogs/api"
import { EcommerceCommonHeader } from "@/components/shared/headers"
import { ScrollableRouteContainer } from "@/components/shared/Container"
import { cn } from "@/lib/utils"
import { ViewSwitcher } from "@/components/shared/views-buttons"
import { useCogsStore } from "@/feature/financial-model/ecommerce/cogs/store"
import { ExportModuleDialog } from "@/feature/export/ecommerce/components/dowload"
import { Show } from "@/components/shared/show"
import { PageBody, PageHeader } from "@/components/shared/page-sections.tsx"

const CogsRoute = () => {
  const { t } = useTranslation(`cogs`)
  const { id: financialModelId } = useParams<{ id: string }>()
  const { financialModel } = useGetDeatiledSingleFinancialModelQuery(
    financialModelId!
  )
  const navigate = useNavigate()
  const location = useLocation()
  const isTotalsPage = useMemo(
    () =>
      location.pathname.includes(
        `/dashboard/financial-model/${financialModelId}/ecommerce/cogs/total`
      ),
    [location.pathname, financialModelId]
  )

  const handleTabsChange = useCallback(
    (value: TabValue) => {
      const tabNavigationMap: Record<TabValue, string> = {
        "set-up": `/dashboard/financial-model/${financialModelId}/ecommerce/cogs`,
        "historical-data": `/dashboard/financial-model/${financialModelId}/ecommerce/cogs/historical-data`,
        "forecast-data": `/dashboard/financial-model/${financialModelId}/ecommerce/cogs/forecast-data`,
        total: `/dashboard/financial-model/${financialModelId}/ecommerce/cogs/total`,
      }
      navigate(tabNavigationMap[value])
    },
    [financialModelId, navigate]
  )

  const { data: financialModelSettings } = useGetCogsSuspenseSettings({
    id: financialModelId!,
    module: `cogs`,
    section: `setup`,
  })

  const view = useCogsStore((state) => state.view)
  const setView = useCogsStore((state) => state.setView)

  return (
    <ScrollableRouteContainer className={`p-0`}>
      <PageHeader className={`pb-0`}>
        <EcommerceCommonHeader
          linkText={t(`cogs.home.header.back`)}
          path={`/dashboard/financial-model/${financialModelId}/ecommerce`}
        />
        <div
          className={cn(`mt-6`, {
            [`flex justify-between items-center`]: isTotalsPage,
          })}
        >
          <div>
            <h1 className="page-title">{t(`cogs.home.header.title`)}</h1>
            <p className="page-subtitle">
              {t(`cogs.home.header.subtitle`)} | {financialModel?.data?.name}
            </p>
          </div>
          <Show when={isTotalsPage}>
            <div className="flex items-center gap-2">
              <ViewSwitcher view={view} onViewChange={setView} />
              <ExportModuleDialog
                financialModelId={financialModelId!}
                options={{ module: `cogs`, view }}
              />
            </div>
          </Show>
        </div>
      </PageHeader>

      <ModuleNavigation
        isSetupCompleted={
          financialModelSettings.status.direct_costs === `done` ||
          financialModelSettings.status.direct_labour === `done`
        }
        handleTabsChange={handleTabsChange}
      >
        <PageBody>
          <Outlet />
        </PageBody>
      </ModuleNavigation>
    </ScrollableRouteContainer>
  )
}

export default CogsRoute
